/**
 * HelpTip — inline contextual help icon (ℹ) with tooltip.
 *
 * - Desktop: tooltip appears on hover and keyboard focus.
 * - Mobile (≤768px): tooltip appears on tap; tap elsewhere to dismiss.
 *
 * Usage:
 *   <HelpTip tipKey="students.name" />
 *
 * The text is pulled from the per-module helpRegistry.  Unknown keys
 * are silently omitted (no icon rendered).
 */

import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { helpRegistry } from './helpRegistry';
import { OverlayPortal } from '../common/OverlayPortal';
import { useAnchoredPosition } from '../../hooks/useAnchoredPosition';

interface HelpTipProps {
  tipKey: string;
  /** Override placement: 'top' (default) | 'bottom' | 'right' */
  placement?: 'top' | 'bottom' | 'right';
}

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  border-radius: 50%;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
`;

const Tooltip = styled.div<{ $top: number; $left: number; $transform: string; $visible: boolean }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: ${({ $transform }) => $transform};
  z-index: ${({ theme }) => theme.zIndex.tooltip};
  background: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.surface};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-width: 240px;
  white-space: normal;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.15s ease;
`;

const ExampleText = styled.span`
  display: block;
  opacity: 0.7;
  margin-top: 2px;
  font-style: italic;
`;

export const HelpTip: React.FC<HelpTipProps> = ({ tipKey, placement = 'top' }) => {
  const entry = helpRegistry[tipKey];
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const position = useAnchoredPosition(anchorRef, placement, visible);

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(false), 100);
  }, []);

  const toggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible((v) => !v);
  }, []);

  if (!entry) return null;

  return (
    <Wrapper>
      <IconBtn
        ref={anchorRef}
        type="button"
        aria-label={`Help: ${entry.label}`}
        data-testid={`candeza-help-${tipKey}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
        </svg>
      </IconBtn>
      <OverlayPortal>
        <Tooltip
          $top={position.top}
          $left={position.left}
          $transform={position.transform}
          $visible={visible}
          role="tooltip"
          data-testid={`candeza-tooltip-${tipKey}`}
        >
          {entry.label}
          {entry.example && <ExampleText>{entry.example}</ExampleText>}
        </Tooltip>
      </OverlayPortal>
    </Wrapper>
  );
};

